import { useState } from "react";

const phoneStores = [
  {
    name: "App Store",
    sub: "iPhone & iPad",
    icon: "ri-apple-fill",
    bg: "bg-stone-900",
    text: "text-white",
    steps: [
      "Open Safari on your iPhone",
      'Tap the Share button (square with arrow)',
      'Scroll down and tap "Add to Home Screen"',
      'Tap "Add" — done! App icon appears on your home screen',
    ],
  },
  {
    name: "Google Play",
    sub: "Android Phones",
    icon: "ri-google-play-fill",
    bg: "bg-emerald-600",
    text: "text-white",
    steps: [
      "Open Chrome browser on your Android phone",
      'Tap the 3-dot menu (⋮) at the top right',
      'Tap "Add to Home screen"',
      'Tap "Add" — the app icon appears instantly',
    ],
  },
  {
    name: "Huawei / Honor",
    sub: "Chinese Phones",
    icon: "ri-smartphone-line",
    bg: "bg-red-600",
    text: "text-white",
    steps: [
      "Open Huawei Browser on your phone",
      'Tap the menu icon (≡) at the bottom',
      'Tap "Add to desktop" or "Add to Home Screen"',
      "Confirm — Crown Global app is now on your phone!",
    ],
  },
  {
    name: "Xiaomi / OPPO / Vivo",
    sub: "All Chinese Android",
    icon: "ri-cellphone-line",
    bg: "bg-amber-500",
    text: "text-white",
    steps: [
      "Open any browser (Chrome, UC, Mi Browser)",
      "Tap the menu icon or share button",
      'Find "Add to Home Screen" or "Add to Desktop"',
      "Tap Add — works on all Chinese Android phones!",
    ],
  },
];

export default function InstallAppSection() {
  const [activeStore, setActiveStore] = useState(0);

  return (
    <section className="py-16 bg-gradient-to-br from-amber-50 via-white to-amber-50">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="w-8 h-px bg-amber-400 inline-block"></span>
            <p className="text-amber-500 text-sm font-bold uppercase tracking-widest">Use on Any Phone</p>
            <span className="w-8 h-px bg-amber-400 inline-block"></span>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3">
            Install Crown Global on Your Phone
          </h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto">
            Works on every phone — iPhone, Android, Huawei, Xiaomi, OPPO, Vivo, Samsung and all Chinese phones. No app store needed, install in seconds for free.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">

          {/* Left: phone mockup + QR */}
          <div className="flex flex-col items-center gap-6">
            <div className="relative">
              <div className="w-56 h-[420px] bg-stone-900 rounded-[2.5rem] p-2 mx-auto">
                <div className="w-full h-full bg-white rounded-[2rem] overflow-hidden relative">
                  <img
                    src="https://public.readdy.ai/ai/img_res/8ef634a2d19cc1915a343db76e8a50e7.jpg"
                    alt="Crown Global App on Phone"
                    className="w-full h-full object-cover object-top"
                  />
                  {/* App icon overlay */}
                  <div className="absolute top-3 left-3 right-3 flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2">
                    <img
                      src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
                      alt="Crown Global"
                      className="w-8 h-8 rounded-lg object-contain"
                    />
                    <div>
                      <p className="text-xs font-bold text-gray-800 leading-none">Crown Global</p>
                      <p className="text-[10px] text-gray-400">Factory &amp; Buyer Platform</p>
                    </div>
                    <div className="ml-auto w-6 h-6 flex items-center justify-center bg-amber-400 rounded-lg">
                      <i className="ri-download-line text-white text-xs"></i>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating badges */}
              <div className="absolute -right-4 top-12 bg-white rounded-xl px-3 py-2 text-center" style={{boxShadow:"0 4px 16px rgba(0,0,0,0.10)"}}>
                <p className="text-amber-500 font-extrabold text-lg leading-none">FREE</p>
                <p className="text-gray-400 text-[10px]">Install</p>
              </div>
              <div className="absolute -left-4 bottom-16 bg-white rounded-xl px-3 py-2 text-center" style={{boxShadow:"0 4px 16px rgba(0,0,0,0.10)"}}>
                <p className="text-green-500 font-extrabold text-lg leading-none">Fast</p>
                <p className="text-gray-400 text-[10px]">30 seconds</p>
              </div>
            </div>

            {/* QR Code area */}
            <div className="bg-white rounded-2xl p-5 text-center w-full max-w-xs" style={{boxShadow:"0 2px 12px rgba(0,0,0,0.07)"}}>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Scan to Open on Phone</p>
              <div className="w-32 h-32 mx-auto bg-gray-100 rounded-xl flex items-center justify-center mb-3 border-2 border-amber-200">
                <div className="text-center">
                  <i className="ri-qr-code-line text-4xl text-amber-400"></i>
                  <p className="text-[10px] text-gray-400 mt-1">QR Code</p>
                </div>
              </div>
              <p className="text-xs text-gray-400">Point your phone camera at this code to open Crown Global instantly</p>
            </div>
          </div>

          {/* Right: step-by-step guide */}
          <div className="flex flex-col gap-4">
            {/* Store selector tabs */}
            <div className="grid grid-cols-2 gap-2">
              {phoneStores.map((store, i) => (
                <button
                  key={store.name}
                  onClick={() => setActiveStore(i)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 transition-all cursor-pointer text-left ${
                    activeStore === i
                      ? "border-amber-400 bg-amber-50"
                      : "border-gray-100 bg-white hover:border-amber-200"
                  }`}
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 ${store.bg}`}>
                    <i className={`${store.icon} ${store.text} text-lg`}></i>
                  </div>
                  <div className="min-w-0">
                    <p className={`text-sm font-bold leading-none mb-0.5 ${activeStore === i ? "text-amber-600" : "text-gray-800"}`}>
                      {store.name}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{store.sub}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Steps */}
            <div className="bg-white rounded-2xl p-6" style={{boxShadow:"0 2px 12px rgba(0,0,0,0.07)"}}>
              <div className="flex items-center gap-3 mb-5">
                <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${phoneStores[activeStore].bg}`}>
                  <i className={`${phoneStores[activeStore].icon} ${phoneStores[activeStore].text} text-xl`}></i>
                </div>
                <div>
                  <h3 className="font-extrabold text-gray-900 text-base">{phoneStores[activeStore].name}</h3>
                  <p className="text-xs text-gray-400">{phoneStores[activeStore].sub}</p>
                </div>
              </div>

              <ul className="flex flex-col gap-4">
                {phoneStores[activeStore].steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-7 h-7 flex items-center justify-center bg-amber-400 text-white text-xs font-extrabold rounded-full shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <p className="text-sm text-gray-700 leading-relaxed">{step}</p>
                  </li>
                ))}
              </ul>

              <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-information-line text-amber-500"></i>
                  <p className="text-xs font-bold text-amber-700">No App Store Required</p>
                </div>
                <p className="text-xs text-amber-600">
                  Crown Global installs directly from your browser — completely free, no account needed to browse. Works offline too!
                </p>
              </div>
            </div>

            {/* Supported phones list */}
            <div className="bg-white rounded-2xl p-5" style={{boxShadow:"0 2px 12px rgba(0,0,0,0.07)"}}>
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Works on All These Phones</p>
              <div className="flex flex-wrap gap-2">
                {[
                  "iPhone", "Samsung", "Huawei", "Xiaomi", "OPPO", "Vivo",
                  "OnePlus", "Honor", "Realme", "Google Pixel", "Nokia", "All Android"
                ].map(phone => (
                  <span key={phone} className="flex items-center gap-1 bg-gray-50 border border-gray-100 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-full">
                    <i className="ri-checkbox-circle-fill text-green-500 text-xs"></i>
                    {phone}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
